// 통합 일정 API 클라이언트 — 각 모듈의 기존 조회 API 를 게이트웨이(/chat-api/api/<모듈>/**)로 부른다.
//
// 이 파일은 **조회만** 한다(쓰기 없음). 어떤 모듈이든 안 떠 있거나(404 MODULE_NOT_ROUTED 등) 실패해도 나머지 출처는 그대로 보이도록
// 출처마다 따로 부르고 결과를 {ok} 로 감싼다(allSettled) — 한 모듈이 합본 전체를 막지 않는다.
//
// 범위는 **본인 사번으로만** 조회한다(부서·전사 범위는 권한 정책이 정해진 뒤 연다 — 통합일정_타임테이블_분석보고서.md §6).
//  · pjt  GET /api/pjt/PJT-09/schedule/load?fromDa&toDa&empCds=본인   (담당 작업 막대)
//  · pjt  GET /api/pjt/PJT-10/todos?progTy=open                        (내 업무 — 서버가 신원으로 본인만 준다)
//  · hr   GET /api/hr/HR-04/leave-requests?empCd=본인&from&to          (휴가)
//  · ops  GET /api/ops/OPS-12/trips?empCd=본인&fromDa&toDa             (출장·항공권 — 2026-10-13 hr 에서 ops 로 이관)
//  · hr   GET /api/hr/HR-03/attend-days?empCd=본인&from&to             (근태 — 확인이 필요한 날만 쓴다)
//
// 주소에 메뉴코드가 들어간다(설계서 27 · 메뉴코드 주소 정책) — 게이트웨이가 그 메뉴의 접근 권한으로 판정한다.
// 그 메뉴 권한이 없는 사용자는 403 이 나는데, 이는 오류가 아니라 「그 출처의 정보가 없음」이다 — 위젯은 조용히 건너뛴다(useSchedule 이 failed 에 넣지 않는다).
import { GW_ORIGIN } from 'yukimushi-component-fe';
import { authorizedFetch } from './auth';
import { failReason } from '../lib/schedule';
import type { ScheduleSource } from '../lib/schedule';

/* 호스트가 게이트웨이 origin 에서 서빙될 때만 절대 경로를 쓰고, 로컬 개발은 상대 경로로 dev 프록시(/chat-api)를 탄다 — modules.ts 와 같다 */
const SERVED_BY_GATEWAY = window.location.hostname.indexOf('gennolab.co.jp') >= 0;
const ROOT = `${SERVED_BY_GATEWAY ? GW_ORIGIN : ''}/chat-api/api`;

/** 한 출처의 응답 — 성공이면 원시 본문, 실패면 이유(화면이 「불러오지 못했습니다」로 보인다) */
export type SourceResult =
  | { source: ScheduleSource; ok: true; body: unknown }
  | { source: ScheduleSource; ok: false; reason: 'not-installed' | 'forbidden' | 'error' };

function qs(params: Record<string, string | undefined>): string {
  const s = new URLSearchParams();
  Object.keys(params).forEach(k => { const v = params[k]; if (v !== undefined && v !== '') s.set(k, v); });
  const t = s.toString();
  return t ? `?${t}` : '';
}

async function get(source: ScheduleSource, path: string, signal?: AbortSignal): Promise<SourceResult> {
  try {
    const res = await authorizedFetch(`${ROOT}${path}`, { signal });
    const bad = failReason(res.status);
    if (bad) return { source, ok: false, reason: bad };
    const type = res.headers.get('content-type') || '';
    // 배포본의 SPA fallback 이 200 + HTML 로 오는 경우 — 데이터가 아니므로 실패로 본다(api/modules.ts 와 같은 이유)
    if (type.indexOf('json') < 0) return { source, ok: false, reason: 'error' };
    return { source, ok: true, body: await res.json() };
  } catch (e) {
    if (e && (e as { name?: string }).name === 'AbortError') throw e;
    return { source, ok: false, reason: 'error' };
  }
}

/** 보이는 기간 [from, to](둘 다 포함)의 일정 원본을 출처별로 받는다. empCd 가 비면 호출하지 않는다 */
export function fetchScheduleSources(empCd: string, from: string, to: string, signal?: AbortSignal): Promise<SourceResult[]> {
  if (!empCd) return Promise.resolve([]);
  return Promise.all([
    get('pjt-task', `/pjt/PJT-09/schedule/load${qs({ fromDa: from, toDa: to, empCds: empCd })}`, signal),
    get('pjt-todo', `/pjt/PJT-10/todos${qs({ progTy: 'open', size: '100' })}`, signal),
    get('hr-leave', `/hr/HR-04/leave-requests${qs({ empCd, from, to, size: '100' })}`, signal),
    get('ops-trip', `/ops/OPS-12/trips${qs({ empCd, fromDa: from, toDa: to })}`, signal),
    get('hr-attend', `/hr/HR-03/attend-days${qs({ empCd, from, to })}`, signal),
  ]);
}
